import {GetServerSideProps} from 'next'
import {useEffect} from 'react'

import {SkipNavigation} from '@defign/ui'
import {Action, sendAULog} from '@financial/common-logs'

import {AssetNotExistError} from '$commons/errors/myAssetErrors'
import {LeftLinkButton, MyAssetHeader} from '$components/common/header'
import ContentOgMetaTag from '$components/insurance/contents/ContentOgMetaTag'
import InsuranceRecommendContent from '$components/insurance/contents/InsuranceRecommendContent'
import InsuranceTipContent from '$components/insurance/contents/InsuranceTipContent'
import MyAssetUnitaryLayout, {ColumnContent, ColumnSnb} from '$components/insurance/layout/MyAssetUnitaryLayout'
import MyassetWrapper from '$components/insurance/layout/MyassetWrapper'
import {INTERNAL_ROUTES} from '$constants/internal-routes'
import InsurancePageProvider, {InsurancePageProps} from '$contexts/insurance/common'
import prefetchMyDataAgreementWebAppEnv from '$gssp/insurance/prefetchMyDataAgreementWebAppEnv'
import {withNonAuthInsuranceGetServerSideProps} from '$hoc/insurance'
import useInsuranceMyAssetQuery from '$hooks/insurance/useInsuranceMyAssetQuery'
import {getContentQuery, getContentsQuery, getContentsBannerQuery} from '$queries/insurance/contentQuery'
import {getDehydratedState, getQueryData, prefetchQueries} from '$server/utils/query'
import {insuranceQueryParser} from '$utils/insurance'

function AuLogSender() {
    const {data} = useInsuranceMyAssetQuery()
    useEffect(() => {
        if (data?.isMydataInsuranceConnected) {
            sendAULog({page: 'insu', area: 'D', service: 'md', code: 'insu_tip_detail_restered'}, Action.View)
        } else {
            sendAULog({page: 'insu', area: 'D', service: 'md', code: 'insu_tip_detail_unrestered'}, Action.View)
        }
    }, [data?.isMydataInsuranceConnected])
    return null
}
export default function InsuranceContentDetail(props: InsurancePageProps) {
    return (
        <InsurancePageProvider {...props}>
            <ContentOgMetaTag />
            <MyassetWrapper>
                <SkipNavigation hasBottomNavi={false} />
                <MyAssetHeader title="내 보험 TIP" blindText>
                    <LeftLinkButton text="내 보험 TIP" href={INTERNAL_ROUTES.INSURANCE_CONTENTS} nClickCode={''} />
                </MyAssetHeader>
                <MyAssetUnitaryLayout columnLayout={true}>
                    <ColumnContent>
                        <InsuranceTipContent />
                    </ColumnContent>
                    <ColumnSnb>
                        <InsuranceRecommendContent />
                    </ColumnSnb>
                </MyAssetUnitaryLayout>
            </MyassetWrapper>
            <AuLogSender />
        </InsurancePageProvider>
    )
}

export const getServerSideProps: GetServerSideProps = withNonAuthInsuranceGetServerSideProps(
    async (ctx, queryClient) => {
        const id = ctx.params?.id

        if (typeof id !== 'string' || Number.isNaN(Number(id))) {
            throw new AssetNotExistError()
        }

        const contentId = +id

        /**
         * 비로그인
         */
        const contentQuery = getContentQuery.serverQuery({
            params: {contentId},
            req: ctx.req,
        })
        const contentsQuery = getContentsQuery.serverQuery({
            params: {onlyRecommend: true, contentId},
            req: ctx.req,
        })

        await prefetchQueries([contentQuery, contentsQuery], queryClient)

        const data = getQueryData(contentQuery, queryClient)

        if (!data) {
            return {
                redirect: {
                    destination: INTERNAL_ROUTES.INSURANCE_CONTENTS,
                    permanent: false,
                },
            }
        }

        /**
         * 로그인
         */
        const params = insuranceQueryParser({...ctx.query})

        try {
            const isWebAppEnv = await prefetchMyDataAgreementWebAppEnv(ctx, queryClient)

            const contentsBannerQuery = getContentsBannerQuery.serverQuery({
                params: {...params, contentId},
                req: ctx.req,
            })

            if (isWebAppEnv) {
                await prefetchQueries([contentsBannerQuery], queryClient)
            }
        } catch {
            /** IGNORE */
        }

        return {
            props: {
                dehydratedState: getDehydratedState(queryClient),
            },
        }
    },
)
